import { useState, useCallback } from 'react';
import type {
  MultiselectProps,
  SelectProps,
} from '@amzn/awsui-components-console';

import type { CreateStringGroupFormData } from '../types';
import { isFormValid } from '../components/createStringGroupValidation';

interface UseCreateStringGroupFormReturn {
  // Field values
  name: string;
  description: string;
  teamName: string;
  selectedCxIds: MultiselectProps.Option[];
  sourceLocale: SelectProps.Option | null;

  // State flags
  submitting: boolean;
  submitError: string | null;
  showValidation: boolean;

  // Field setters
  setName: (value: string) => void;
  setDescription: (value: string) => void;
  setTeamName: (value: string) => void;
  setSelectedCxIds: (options: MultiselectProps.Option[]) => void;
  setSourceLocale: (option: SelectProps.Option | null) => void;
  setSubmitError: (error: string | null) => void;

  // Actions
  resetForm: () => void;
  handleSubmit: (
    onSubmit: (data: CreateStringGroupFormData) => Promise<void>,
    onSuccess: () => void,
    cxFamilyId: string,
  ) => Promise<void>;
}

/**
 * Encapsulates all form state, validation, and submission logic for the
 * Create String Group modal.
 */
export function useCreateStringGroupForm(): UseCreateStringGroupFormReturn {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [teamName, setTeamName] = useState('');
  const [selectedCxIds, setSelectedCxIds] = useState<MultiselectProps.Option[]>(
    [],
  );
  const [sourceLocale, setSourceLocale] = useState<SelectProps.Option | null>(
    null,
  );

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showValidation, setShowValidation] = useState(false);

  const resetForm = useCallback(() => {
    setName('');
    setDescription('');
    setTeamName('');
    setSelectedCxIds([]);
    setSourceLocale(null);
    setSubmitting(false);
    setSubmitError(null);
    setShowValidation(false);
  }, []);

  const handleSubmit = useCallback(
    async (
      onSubmit: (data: CreateStringGroupFormData) => Promise<void>,
      onSuccess: () => void,
      cxFamilyId: string,
    ) => {
      setShowValidation(true);
      setSubmitError(null);

      const formData: CreateStringGroupFormData = {
        name: name.trim(),
        description: description.trim(),
        teamName: teamName.trim(),
        cxFamilyId,
        cxIds: selectedCxIds,
        sourceLocale,
      };

      if (!isFormValid(formData)) {
        return;
      }

      setSubmitting(true);
      try {
        await onSubmit(formData);
        resetForm();
        onSuccess();
      } catch (error) {
        setSubmitError(
          error instanceof Error
            ? error.message
            : 'Failed to create string group. Please try again.',
        );
      } finally {
        setSubmitting(false);
      }
    },
    [name, description, teamName, selectedCxIds, sourceLocale, resetForm],
  );

  return {
    name,
    description,
    teamName,
    selectedCxIds,
    sourceLocale,
    submitting,
    submitError,
    showValidation,
    setName,
    setDescription,
    setTeamName,
    setSelectedCxIds,
    setSourceLocale,
    setSubmitError,
    resetForm,
    handleSubmit,
  };
}
